import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action, select, Store } from '@ngrx/store'
import { EMPTY } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators'

import { AppointmentAdapter } from '../../shared/adapters/AppointmentAdapter'
import { PayloadedAction } from '../../shared/types'
import { AAAStore } from '../../store/root-reducer'
import { setIssueOptionSelected } from '../issue/issue.actions'
import { selectIssueOptions, selectIssueOptionSelected, selectSiblingsOfSelectedOption } from '../issue/issue.selectors'
import { IssueOption, IssueOptionSelected, IssueOptionsResponse } from '../issue/issue.types'
import { selectBreakdownLocation, selectLocationClub } from '../location/location.selectors'
import { BreakdownLocation, Coordinates } from '../location/location.types'
import { SET_SERVICING_CLUB_CONFIGS } from '../servicing-club/servicing-club.actions'
import {
  APPOINTMENT_AVAILABILITY,
  APPOINTMENT_AVAILABILITY_REQUESTS_COMPLETED,
  APPOINTMENT_DATE,
  APPOINTMENT_SELECTED,
  appointmentDateFailure,
  appointmentDateRequestsCompleted,
  appointmentDateSuccess,
  requestAppointmentAvailability,
  requestAppointmentDate,
  resetAppointmentAvailability,
  resetSelectedAppointment
} from './appointment.actions'
import { selectAppointmentAvailability, selectIsAppointmentRequestAllowed, selectSelectedAppointment } from './appointment.selectors'
import { AppointmentService } from './appointment.service'
import { AppointmentRequest, AppointmentResponse } from './appointment.types'
import { AppointmentUtils, MAX_DAYS_REQUEST } from './appointment.utils'

@Injectable()
export class AppointmentEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private appointmentService: AppointmentService,
    private appointmentAdapter: AppointmentAdapter,
    private appointmentUtils: AppointmentUtils,
  ) {}

  triggerRequestAppointmentAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_SERVICING_CLUB_CONFIGS.SUCCESS),
      withLatestFrom(
        this.store$.pipe(select(selectIsAppointmentRequestAllowed)),
        this.store$.pipe(select(selectBreakdownLocation))
      ),
      filter(([_, isRequestAllowed]) => isRequestAllowed),
      switchMap(([_, _2, breakdownLocation]: [Action, boolean, BreakdownLocation]) => [
        resetAppointmentAvailability(),
        requestAppointmentAvailability({
          payload: {
            latitude: Number(breakdownLocation.latitude),
            longitude: Number(breakdownLocation.longitude),
          },
        })
      ])
    )
  )

  resetSelectedAppointmentIfApplicable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APPOINTMENT_AVAILABILITY_REQUESTS_COMPLETED),
      withLatestFrom(
        this.store$.pipe(select(selectSelectedAppointment)),
        this.store$.pipe(select(selectAppointmentAvailability))
      ),
      filter(([_, selectedAppointment, appointmentAvailability]) => 
        !this.appointmentUtils.isAppointmentSlotAvailable(selectedAppointment, appointmentAvailability)
      ),
      map(() => resetSelectedAppointment())
    )
  )

  checkAppointmentDateRequestsCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APPOINTMENT_DATE.SUCCESS),
      withLatestFrom(this.store$.pipe(select(selectAppointmentAvailability))),
      filter(([_, availability]) => availability.dates.length === MAX_DAYS_REQUEST),
      map(() => appointmentDateRequestsCompleted())
    )
  )

  requestAppointmentAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof requestAppointmentAvailability>>(APPOINTMENT_AVAILABILITY),
      switchMap((action: PayloadedAction<Coordinates>) => {
        const nextDates = this.appointmentUtils.calculateNextDates()
        return [
          ...nextDates.map(date =>
            requestAppointmentDate({
              payload: {
                ...action.payload,
                date
              }
            })
          )
        ]
      })
    )
  )

  requestAppointmentDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof requestAppointmentDate>>(APPOINTMENT_DATE.REQUEST),
      withLatestFrom(this.store$.pipe(select(selectLocationClub))),
      mergeMap(([action, locationClub]: [PayloadedAction<AppointmentRequest>, string]) =>
        this.appointmentService.getAppointmentTimes$(locationClub, action.payload).pipe(
          map((response: AppointmentResponse) => {
            const adaptedData = this.appointmentAdapter.adapt({
              ...response,
              ...action.payload,
            })
            return appointmentDateSuccess({ payload: adaptedData })
          }),
          catchError((error) => {
            console.error(error)
            return [appointmentDateFailure({ payload: action.payload.date })]
          })
        )
      )
    )
  )

  changeIssueOptionBasedOnAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APPOINTMENT_SELECTED),
      withLatestFrom(
        this.store$.pipe(select(selectIssueOptions)),
        this.store$.pipe(select(selectSiblingsOfSelectedOption)),
        this.store$.pipe(select(selectIssueOptionSelected)),
      ),
      switchMap(([_, issueOptions, siblingsOfSelectedOption, issueOptionSelected]: [never, IssueOptionsResponse, IssueOption[], IssueOptionSelected | null]) => {
        const optionCodes = siblingsOfSelectedOption.map(option => option.optionCode);
        const newIssueOption = issueOptions?.options
          .flatMap(option => [option, ...(option.options ?? [])])
          .find(option => optionCodes.includes(option.optionCode));
        // Re-dispatching for the option that's already selected would re-trigger
        // processIssueOptionSelected$ (navigation, partial-call update, and — the bug this
        // guards against — re-opening the issue advisory dialog) for no actual change.
        if (newIssueOption && newIssueOption.optionCode !== issueOptionSelected?.option?.optionCode) {
          return [
            setIssueOptionSelected({
              payload: {
                option: newIssueOption,
                composedService: newIssueOption.service
              }
            })
          ]
        }
        return EMPTY
      })
    )
  )

}
